import { useState, useRef } from 'react';
import { geocodePostcode } from '../lib/geocoding';
import { PostcodeInput } from '../components/PostcodeInput';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAppAuth } from '../hooks/useAppAuth';
import {
  Settings, Mic, MicOff, Bell, BellOff, Plus, Trash2,
  AlertCircle, CheckCircle, ToggleLeft, ToggleRight, Mail, MessageSquare, Smartphone, Zap,
} from 'lucide-react';
import { createApiClient, profileService, jobAlertsService, notificationsApi } from '../services/api';
import { useRefresh } from '../hooks/useRefresh';
import { RefreshButton } from '../components/RefreshButton';
import { VehicleType } from '../types';
import type { JobAlertPreference } from '../types';
import type { NotificationPreference } from '../types/modules';

const RADII = [5, 10, 15, 25, 50];

const VEHICLE_OPTIONS: { label: string; value: VehicleType | '' }[] = [
  { label: 'Any vehicle',  value: '' },
  { label: 'Motorcycle',   value: VehicleType.Motorcycle },
  { label: 'Car',          value: VehicleType.Car },
  { label: 'Small Van',    value: VehicleType.SmallVan },
  { label: 'Midi Van',     value: VehicleType.MidiVan },
  { label: 'LWB Van',      value: VehicleType.LWBVan },
  { label: 'Luton Van',    value: VehicleType.LutonVan },
  { label: '7.5T',         value: VehicleType.Tonne7_5 },
  { label: '12T',          value: VehicleType.Tonne12 },
  { label: '18T',          value: VehicleType.Tonne18 },
  { label: '26T',          value: VehicleType.Tonne26 },
  { label: 'Artic',        value: VehicleType.Artic },
];

function AlertCard({
  alert,
  onToggle,
  onDelete,
  toggling,
  deleting,
}: {
  alert: JobAlertPreference;
  onToggle: () => void;
  onDelete: () => void;
  toggling: boolean;
  deleting: boolean;
}) {
  const vehicleLabel = VEHICLE_OPTIONS.find(v => v.value === alert.vehicleType)?.label ?? 'Any vehicle';

  return (
    <div className={`card p-4 transition-opacity ${!alert.isEnabled ? 'opacity-60' : ''}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
            alert.isEnabled ? 'bg-blue-100' : 'bg-gray-100'
          }`}>
            {alert.isEnabled
              ? <Bell className="w-4 h-4 text-blue-600" />
              : <BellOff className="w-4 h-4 text-gray-400" />
            }
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-gray-900 uppercase tracking-wide">{alert.postcode}</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                {alert.radiusMiles} mi
              </span>
              {alert.vehicleType !== null && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700">
                  {vehicleLabel}
                </span>
              )}
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                alert.isEnabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
              }`}>
                {alert.isEnabled ? 'Active' : 'Paused'}
              </span>
            </div>
            {alert.lastMatchedAt && (
              <p className="text-xs text-gray-400 mt-1">
                Last match {new Date(alert.lastMatchedAt).toLocaleDateString()}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={onToggle}
            disabled={toggling}
            title={alert.isEnabled ? 'Pause alert' : 'Activate alert'}
            className="p-2 rounded-lg text-gray-500 hover:text-blue-600 hover:bg-blue-50 transition-colors disabled:opacity-40"
          >
            {alert.isEnabled
              ? <ToggleRight className="w-5 h-5 text-blue-600" />
              : <ToggleLeft className="w-5 h-5" />
            }
          </button>
          <button
            onClick={onDelete}
            disabled={deleting}
            title="Delete alert"
            className="p-2 rounded-lg text-gray-500 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-40"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PreferencesPage() {
  const { getToken, account } = useAppAuth();
  const qc = useQueryClient();

  const api = createApiClient(getToken);
  const profSvc = profileService(api);
  const alertSvc = jobAlertsService(api);

  //  Profile (for voiceChatEnabled + role fallback) 
  const { data: profile } = useQuery({
    queryKey: ['profile'],
    queryFn: () => profSvc.getProfile(),
    enabled: !!account,
  });

  const tokenRole = ((account?.idTokenClaims as Record<string, unknown> | undefined)?.extension_Role as string | undefined)?.toLowerCase();
  const roleLower = tokenRole ?? profile?.role?.toLowerCase();
  const canUseAlerts = roleLower === 'carrier' || roleLower === 'workshop';
  const isWorkshop  = roleLower === 'workshop';

  const WORKSHOP_SERVICES = [
    { value: 'mot',           label: 'MOT' },
    { value: 'annual_service',label: 'Annual Service / Inspection' },
    { value: 'servicing',     label: 'Servicing' },
    { value: 'repairs',       label: 'General Repairs' },
    { value: 'tyres',         label: 'Tyres' },
    { value: 'diagnostics',   label: 'Diagnostics' },
    { value: 'bodywork',      label: 'Body Works' },
    { value: 'paintwork',     label: 'Paint Works' },
    { value: 'recovery',      label: 'Recovery' },
    { value: 'mobile_assist', label: 'Mobile Roadside Assistance' },
  ];

  const storedServices: string[] = profile?.workshopServiceTypes ?? [];
  const [selectedServices, setSelectedServices] = useState<string[]>(storedServices);

  const servicesMut = useMutation({
    mutationFn: (next: string[]) =>
      profSvc.updateProfile({ ...(profile as any), workshopServiceTypes: next }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['profile'] }),
  });

  const toggleService = (val: string) => {
    const next = selectedServices.includes(val)
      ? selectedServices.filter(s => s !== val)
      : [...selectedServices, val];
    setSelectedServices(next);
    servicesMut.mutate(next);
  };

  //  Notification preferences 
  const { data: notifPrefs } = useQuery<NotificationPreference>({
    queryKey: ['notif-prefs'],
    queryFn: async () => {
      const token = await getToken();
      return notificationsApi.getPrefs(token!);
    },
    enabled: !!account,
  });

  const notifPrefMut = useMutation({
    mutationFn: async (prefs: Partial<NotificationPreference>) => {
      const token = await getToken();
      return notificationsApi.savePrefs(prefs, token!);
    },
    onSuccess: (updated) => qc.setQueryData(['notif-prefs'], updated),
  });

  const toggleNotifPref = (key: keyof Pick<NotificationPreference, 'emailEnabled' | 'smsEnabled' | 'pushEnabled' | 'marketingEmail'>) => {
    if (!notifPrefs) return;
    notifPrefMut.mutate({ ...notifPrefs, [key]: !notifPrefs[key] });
  };

  const voiceMut = useMutation({
    mutationFn: (enabled: boolean) =>
      profSvc.updateProfile({
        firstName:         profile?.firstName ?? '',
        lastName:          profile?.lastName  ?? '',
        isBusinessAccount: profile?.isBusinessAccount ?? false,
        voiceChatEnabled:  enabled,
      }),
    onSuccess: (updated) => qc.setQueryData(['profile'], updated),
  });

  //  Job Alerts 
  const [postcode, setPostcode]       = useState('');
  const [radius, setRadius]           = useState(10);
  const [vehicleType, setVehicleType] = useState<VehicleType | ''>('');
  const [formError, setFormError]     = useState<string | null>(null);
  const postcodeRef = useRef<HTMLInputElement>(null);

  const { data: alerts = [], isLoading: alertsLoading, refetch: refetchAlerts } = useQuery({
    queryKey: ['job-alerts'],
    queryFn: () => alertSvc.getAll(),
    enabled: !!account && canUseAlerts,
  });

  const { isRefreshing, lastRefreshed, refresh } = useRefresh(refetchAlerts);

  const createMut = useMutation({
    mutationFn: async () => {
      const { lat, lng } = await geocodePostcode(postcode.trim());
      return alertSvc.create({
        postcode:    postcode.trim().toUpperCase(),
        centroidLat: lat,
        centroidLng: lng,
        radiusMiles: radius,
        vehicleType: vehicleType !== '' ? vehicleType : undefined,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['job-alerts'] });
      setPostcode('');
      setRadius(10);
      setVehicleType('');
      setFormError(null);
      postcodeRef.current?.focus();
    },
    onError: (err: Error) => setFormError(err.message),
  });

  const toggleMut = useMutation({
    mutationFn: (id: string) => alertSvc.toggle(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['job-alerts'] }),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => alertSvc.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['job-alerts'] }),
  });

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!postcode.trim()) return;
    if (alerts.length >= 10) {
      setFormError('You can save up to 10 job alerts. Remove one to add another.');
      return;
    }
    setFormError(null);
    createMut.mutate();
  };

  const activeCount = alerts.filter(a => a.isEnabled).length;
  const pausedCount = alerts.filter(a => !a.isEnabled).length;
  const voiceEnabled = profile?.voiceChatEnabled ?? false;

  return (
    <div className="page-shell space-y-8">

      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
            <Settings className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">My Preferences</h1>
            <p className="text-sm text-gray-500">Voice assistant and job alert settings.</p>
          </div>
        </div>
        <RefreshButton onRefresh={refresh} isRefreshing={isRefreshing} lastRefreshed={lastRefreshed} />
      </div>

      {/* Voice Assistant */}
      <section className="card p-5">
        <div className="flex items-center gap-3 mb-1">
          {voiceEnabled
            ? <Mic className="w-4 h-4 text-indigo-600" />
            : <MicOff className="w-4 h-4 text-gray-400" />
          }
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Voice Assistant</h2>
        </div>
        <p className="text-xs text-gray-400 mb-4">
          When enabled the assistant will speak responses aloud. Uses your device's speaker.
        </p>

        <div className="flex items-center justify-between">
          <label htmlFor="voiceToggle" className="text-sm text-gray-700 cursor-pointer select-none">
            Enable voice audio in chat
          </label>
          <button
            id="voiceToggle"
            role="switch"
            aria-checked={voiceEnabled}
            disabled={voiceMut.isPending || !profile}
            onClick={() => voiceMut.mutate(!voiceEnabled)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none
              ${voiceEnabled ? 'bg-indigo-600' : 'bg-gray-200'}
              disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform
              ${voiceEnabled ? 'translate-x-6' : 'translate-x-1'}`}
            />
          </button>
        </div>

        {voiceMut.isSuccess && (
          <div className="flex items-center gap-2 mt-3 text-xs text-green-700">
            <CheckCircle className="w-3.5 h-3.5" /> Saved
          </div>
        )}
      </section>

      {/* Notification Preferences */}
      <section className="card p-5 space-y-4">
        <div className="flex items-center gap-3 mb-1">
          <Bell className="w-4 h-4 text-blue-600" />
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Notification Preferences</h2>
        </div>
        <p className="text-xs text-gray-400">Choose how you'd like to receive notifications from TransitDeal.</p>

        {[
          { key: 'emailEnabled' as const, label: 'Email notifications', icon: <Mail className="w-4 h-4 text-gray-500" /> },
          { key: 'smsEnabled'   as const, label: 'SMS notifications',   icon: <MessageSquare className="w-4 h-4 text-gray-500" /> },
          { key: 'pushEnabled'  as const, label: 'Push notifications',  icon: <Smartphone className="w-4 h-4 text-gray-500" /> },
          { key: 'marketingEmail' as const, label: 'Marketing emails',  icon: <Mail className="w-4 h-4 text-gray-400" /> },
        ].map(({ key, label, icon }) => {
          const enabled = notifPrefs?.[key] ?? false;
          return (
            <div key={key} className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
                {icon} {label}
              </label>
              <button
                role="switch"
                aria-checked={enabled}
                disabled={notifPrefMut.isPending || !notifPrefs}
                onClick={() => toggleNotifPref(key)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none
                  ${enabled ? 'bg-blue-600' : 'bg-gray-200'}
                  disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform
                  ${enabled ? 'translate-x-6' : 'translate-x-1'}`}
                />
              </button>
            </div>
          );
        })}

        {notifPrefMut.isSuccess && (
          <div className="flex items-center gap-2 text-xs text-green-700">
            <CheckCircle className="w-3.5 h-3.5" /> Preferences saved
          </div>
        )}
      </section>

      {/* Auto-Quote — carriers and workshops only; a customer has no rate card. */}
      {canUseAlerts && (
        <section className="card p-5">
          <div className="flex items-center gap-3 mb-1">
            <Zap className="w-4 h-4 text-blue-600" />
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Auto-Quote</h2>
          </div>
          <p className="text-xs text-gray-500 mb-4">
            Set your prices once and we'll quote matching jobs on your behalf the moment they're
            posted. You can change any amount before it's accepted.
          </p>
          <a
            href="/auto-quote"
            data-testid="preferences-auto-quote-link"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-colors"
          >
            <Zap className="w-4 h-4" />
            Set up auto-quote
          </a>
        </section>
      )}

      {/* Workshop Services */}
      {isWorkshop && (
        <section className="card p-5">
          <div className="flex items-center gap-3 mb-1">
            <Settings className="w-4 h-4 text-blue-600" />
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Services Offered</h2>
          </div>
          <p className="text-xs text-gray-500 mb-4">
            Select the services your workshop provides. Job alerts and repair notifications will only reach you for matching service types.
          </p>
          <div className="flex flex-wrap gap-2">
            {WORKSHOP_SERVICES.map(s => {
              const active = selectedServices.includes(s.value);
              return (
                <button
                  key={s.value}
                  type="button"
                  onClick={() => toggleService(s.value)}
                  disabled={servicesMut.isPending}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors disabled:opacity-60 ${
                    active
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'
                  }`}
                >
                  {active ? '✓ ' : ''}{s.label}
                </button>
              );
            })}
          </div>
          {servicesMut.isSuccess && (
            <p className="text-xs text-green-600 flex items-center gap-1 mt-2">
              <CheckCircle className="w-3 h-3" />Saved
            </p>
          )}
        </section>
      )}

      {canUseAlerts && <section className="space-y-5">
        <div>
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-2">
            <Bell className="w-4 h-4 text-blue-600" /> Job Alerts
          </h2>
        </div>

        <>
            <p className="text-xs text-gray-500">
              Save up to 10 postcode areas. You'll be notified when a job is posted within your chosen distance.
            </p>

            {/* Stats strip */}
            {alerts.length > 0 && (
              <div className="flex gap-4 text-sm">
                <span className="flex items-center gap-1.5 text-green-700 font-medium">
                  <Bell className="w-3.5 h-3.5" /> {activeCount} active
                </span>
                {pausedCount > 0 && (
                  <span className="flex items-center gap-1.5 text-gray-400">
                    <BellOff className="w-3.5 h-3.5" /> {pausedCount} paused
                  </span>
                )}
                <span className="text-gray-300">Â·</span>
                <span className="text-gray-400">
                  {10 - alerts.length} slot{10 - alerts.length !== 1 ? 's' : ''} remaining
                </span>
              </div>
            )}

            {/* Create form */}
            <form onSubmit={handleCreate} className="card p-5 space-y-4">
              <h3 className="font-semibold text-gray-800 flex items-center gap-2 text-sm">
                <Plus className="w-4 h-4 text-blue-500" /> Add Alert Area
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Postcode or district</label>
                  <PostcodeInput
                    value={postcode}
                    onChange={v => { setPostcode(v); setFormError(null); }}
                    inputRef={postcodeRef}
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Type a town name (e.g. Leeds, Birmingham) or postcode/district (e.g. LS1, SW1A 1AA) and pick from the dropdown.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Radius</label>
                  <div className="flex gap-2 flex-wrap">
                    {RADII.map(r => (
                      <button
                        key={r}
                        type="button"
                        onClick={() => setRadius(r)}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                          radius === r
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                        }`}
                      >
                        {r} mi
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Vehicle type <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <select
                    value={vehicleType}
                    onChange={e => setVehicleType(e.target.value as VehicleType | '')}
                    className="form-input"
                  >
                    {VEHICLE_OPTIONS.map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {formError && (
                <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />{formError}
                </div>
              )}
              {createMut.isSuccess && (
                <div className="flex items-start gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                  <CheckCircle className="w-4 h-4 shrink-0 mt-0.5" />Alert saved. You'll be notified when matching jobs arrive.
                </div>
              )}

              <button
                type="submit"
                disabled={createMut.isPending || !postcode.trim()}
                className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700
                           disabled:bg-blue-400 text-white font-medium py-2.5 px-4 rounded-lg transition-colors"
              >
                {createMut.isPending
                  ? <><span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />Saving</>
                  : <><Bell className="w-4 h-4" />Save Alert</>
                }
              </button>
            </form>

            {/* Alert list */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3 text-sm">Saved Alerts</h3>
              {alertsLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin w-6 h-6 border-4 border-blue-500 border-t-transparent rounded-full" />
                </div>
              ) : alerts.length === 0 ? (
                <div className="card p-8 text-center text-gray-400">
                  <Bell className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p className="text-sm font-medium">No alerts yet</p>
                  <p className="text-xs mt-1">Add a postcode above to start receiving job notifications.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {alerts.map(a => (
                    <AlertCard
                      key={a.id}
                      alert={a}
                      toggling={toggleMut.isPending && toggleMut.variables === a.id}
                      deleting={deleteMut.isPending && deleteMut.variables === a.id}
                      onToggle={() => toggleMut.mutate(a.id)}
                      onDelete={() => deleteMut.mutate(a.id)}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* How it works */}
            <div className="card p-5 bg-blue-50 border-blue-100 space-y-2">
              <h3 className="text-sm font-semibold text-blue-900">How job alerts work</h3>
              <ul className="text-xs text-blue-800 space-y-1 list-disc list-inside">
                <li>When a job is posted within your postcode and radius you're notified immediately.</li>
                <li>On mobile you'll receive a push notification and a voice announcement.</li>
                <li>Tell the AI assistant: <em>"Alert me for van jobs in LS1 within 10 miles."</em></li>
                <li>Pause an alert temporarily  it won't fire while paused.</li>
              </ul>
            </div>
        </>
      </section>}
    </div>
  );
}

